import { takeLatest, put, all, call, takeEvery } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as actions from "./actions";
import { db } from "helpers/firebase/firebaseConfig";
import { doc, collection, getDocs,getDoc, addDoc, updateDoc, deleteDoc, query, where, writeBatch } from "firebase/firestore";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

// Node API base
const NODE_API = "https://node-server-eta-coral.vercel.app";
const NODE_API_KEY = process.env.REACT_APP_NODE_API_KEY;

// Helper: create ERP Item via node proxy
async function createERPItem(itemPayload) {
  const res = await fetch(`${NODE_API}/item`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      "x-api-key": NODE_API_KEY, // add API key header to pass validateApiKey
    },
    body: JSON.stringify(itemPayload),
  });
  const json = await res.json();
  if (!res.ok || json?.error || json?.exc_type) {
    const message = json?.message || json?.error || json?._server_messages || "ERP Item creation failed";
    throw new Error(typeof message === "string" ? message : JSON.stringify(message));
  }
  return json?.data || json;
}

// File -> base64 (no data: prefix)
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || "";
    const base64 = String(result).replace(/^data:[^;]+;base64,/, '');
    resolve(base64);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Worker Saga for adding a new service
async function addService(newService) {
  try {
    // Validate required fields early
    if (!newService.provider_id) {
      throw new Error("Missing provider_id: Cannot create service without a valid provider.");
    }
    if (!newService.img_file) {
      throw new Error("Missing image file.");
    }

    const img = newService.img_file;

    // Upload image via Node server (avoid browser->Spaces CORS)
    const fileBase64 = await fileToBase64(img);
    const uploadRes = await fetch(`${NODE_API}/upload-service-image`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": NODE_API_KEY,
      },
      body: JSON.stringify({
        fileName: img.name,
        contentType: img.type || "application/octet-stream",
        fileBase64,
        folder: "services",
      }),
    });
    if (!uploadRes.ok) {
      const err = await uploadRes.text();
      throw new Error(err || "Image upload failed");
    }
    const { url: imgUrl, key: imgKey } = await uploadRes.json();

    const serviceData = {
      service_provider_auth_id: newService.provider_id,
      service_provider_id: newService.provider_id,
      name: newService.name,
      service_price: Number(newService.service_price),
      img_url: imgUrl,
      img_id: imgKey,
      description: newService.description,
      service_type: newService.service_type,
      service_category: newService.category_type,
      avg_execute_time: Number(newService.avg_execute_time),
      at_home_service_cost: Number(newService.at_home_service_price),
      createdAt: new Date().toISOString(),
      is_deleted: false,
      status: "active",
      // ...erp_item_code fields will be added after ERP creation...
    };

    const docRef = await addDoc(collection(db, "service_provider_services"), serviceData);

    // Create ERP Item (best-effort)
    try {
      const codeSuffix = docRef.id.slice(0, 6).toUpperCase();
      const item_code = `DB-SRV-${codeSuffix}`;
      const erpPayload = {
        item_code,
        item_name: newService.name,
        item_group: "Services",
        stock_uom: "Nos",
        is_stock_item: 0,
        is_sales_item: 1,
        description: newService.description || "",
        item_tax_template: "Standard Vat 15% - DS",
      };

      await createERPItem(erpPayload);
      await updateDoc(doc(db, "service_provider_services", docRef.id), {
        erp_item_code: erpPayload.item_code,
        erp_item_group: erpPayload.item_group,
        stock_uom: erpPayload.stock_uom,
        item_tax_template: erpPayload.item_tax_template,
        erp_item_created_at: new Date().toISOString(),
      });

      return {
        id: docRef.id,
        ...serviceData,
        erp_item_code: erpPayload.item_code,
        erp_item_group: erpPayload.item_group,
        stock_uom: erpPayload.stock_uom,
        item_tax_template: erpPayload.item_tax_template,
      };
    } catch (erpErr) {
      console.error("ERP Item creation failed:", erpErr.message);
      // Continue without ERP link
      return { id: docRef.id, ...serviceData };
    }
  } catch (error) {
    console.error("Error adding service:", error);
    toast.error(error.message || "Failed to add service");
    throw error;
  }
}

function* addServiceSaga(action) {
  try {
    const response = yield call(addService, action.payload);
    yield put(actions.addServiceSuccess(response));
    toast.success("Service added successfully");
  } catch (error) {
    console.log("add services",error)
    // yield put(actions.addServiceFailure(error.message));
  }
}

// Worker Saga for updating a service
async function updateServiceData(id, updatedService) {
  try {
    const serviceRef = doc(db, "service_provider_services", id);
    
    const updateData = {
      name: updatedService.name,
      service_price: Number(updatedService.service_price),
      description: updatedService.description,
      service_type: updatedService.service_type,
      service_category: updatedService.service_category,
      avg_execute_time: Number(updatedService.avgExecuteTime),
      at_home_service_cost: Number(updatedService.atHomeServiceCost),
      updatedAt: new Date().toISOString()
    };

    await updateDoc(serviceRef, updateData);
    
    return { id, ...updateData };
  } catch (error) {
    console.error("Error updating service:", error);
    toast.error("Failed to update service");
    throw error;
  }
}

function* updateServiceSaga(action) {
  try {
    const response = yield call(updateServiceData, action.payload.id, action.payload);
    yield put(actions.updateServiceSuccess(response));
    toast.success("Service updated successfully");
  } catch (error) {
    // yield put(actions.updateServiceFailure(error.message));
  }
}

// Worker Saga for removing a service

async function removeService(id) {
  const deletionBatch = writeBatch(db);

  try {
    // 1. Get service document
    const serviceRef = doc(db, "service_provider_services", id);
    const serviceDoc = await getDoc(serviceRef);
    
    if (!serviceDoc.exists()) {
      throw new Error("Service not found");
    }
    const serviceData = serviceDoc.data();

    // 2. Create audit log of deletion (for reference)
    const deletionData = {
      service_id: id,
      service_name: serviceData.name,
      service_data: serviceData,
      deleted_at: new Date().toISOString(),
      deleted_by: "Provider",
      deletion_type: "soft_delete",
      note: "Service marked as deleted by provider. All related bookings, invoices, and references are preserved for historical records."
    };

    // 3. Create audit log document
    const logRef = doc(collection(db, "services_deletion_logs"));
    deletionBatch.set(logRef, deletionData);

    // 4. Mark service as deleted (SOFT DELETE - not hard delete)
    deletionBatch.update(serviceRef, {
      is_deleted: true,
      status: "archived",
      deleted_at: new Date().toISOString(),
      deleted_by: "Provider",
      updated_at: new Date().toISOString()
    });

    // 5. Commit the soft delete operation
    await deletionBatch.commit();

    // 6. Optional: Delete service image from storage (via Node server)
    if (serviceData.img_id) {
      try {
        const delRes = await fetch(`${NODE_API}/delete-service-image`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": NODE_API_KEY,
          },
          body: JSON.stringify({ key: serviceData.img_id }),
        });
        if (!delRes.ok) {
          const err = await delRes.text();
          console.warn("Image deletion error - service remains accessible:", err);
        }
      } catch (storageError) {
        console.warn("Image deletion error - service remains accessible:", storageError);
      }
    }

    // 7. Return success - service marked as deleted but data preserved
    return id;

  } catch (error) {
    console.error("Soft delete failed:", error);
    throw new Error(`Failed to archive service: ${error.message}`);
  }
}

function* removeServiceSaga(action) {
  try {
    const serviceId = action.payload;
    yield call(removeService, serviceId);
    yield put(actions.removeServiceSuccess(serviceId));
    toast.success("Service  deleted successfully");
  } catch (error) {
    // yield put(actions.removeServiceFailure(error.message));
    toast.error(`Failed to delete service: ${error.message}`);
  }
}

// Worker Saga for fetching services
async function fetchServices() {
  try {
    const provider_id = Cookies.get("usersessionId", {
      secure: true,
      sameSite: "strict",
      path: "/",
    });

    if (!provider_id) {
      throw new Error("No provider ID found");
    }

    const q = query(
      collection(db, "service_provider_services"),
      where("service_provider_auth_id", "==", provider_id)
    );

    const querySnapshot = await getDocs(q);
    const allServices = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter out soft-deleted services (services without is_deleted field are considered active)
    return allServices.filter(service => service.is_deleted !== true);
  } catch (error) {
    console.error("Error fetching services:", error);
    throw error;
  }
}

function* fetchServicesSaga() {
  try {
    const services = yield call(fetchServices);
    yield put(actions.fetchServicesSuccess(services));
  } catch (error) {
    // yield put(actions.fetchServicesFailure(error.message));
  }
}

// Worker Saga for fetching categories
async function fetchCategories() {
  try {
    const querySnapshot = await getDocs(collection(db, "categories"));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
}

function* fetchCategoriesSaga() {
  try {
    const categories = yield call(fetchCategories);
    yield put(actions.fetchCategoriesSuccess(categories));
  } catch (error) {
    // yield put(actions.fetchCategoriesFailure(error.message));
  }
}

// Worker Saga for fetching service types
async function fetchTypes() {
  try {
    const querySnapshot = await getDocs(collection(db, "service_type"));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error fetching service types:", error);
    throw error;
  }
}

function* fetchTypesSaga() {
  try {
    const types = yield call(fetchTypes);

    yield put(actions.fetchTypesSuccess(types));
  } catch (error) {
    // yield put(actions.fetchTypesFailure(error.message));
  }
}

// Watcher Saga
function* watchServiceSaga() {
  yield all([
    takeLatest(actionTypes.ADD_SERVICE, addServiceSaga),
    takeLatest(actionTypes.UPDATE_SERVICE, updateServiceSaga),
    takeLatest(actionTypes.REMOVE_SERVICE, removeServiceSaga),
    takeEvery(actionTypes.FETCH_SERVICES_REQUEST, fetchServicesSaga),
    takeLatest(actionTypes.FETCH_CATEGORIES_REQUEST, fetchCategoriesSaga),
    takeLatest(actionTypes.FETCH_TYPES_REQUEST, fetchTypesSaga),
  ]);
}

export default watchServiceSaga;
